import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ALL_MODULES } from '../config/demoPackages';
import { 
  Receipt, Boxes, TrendingUp, Users, Megaphone, Headphones, ClipboardList, FileSignature,
  UserCog, Clock, DollarSign, Vault, Landmark, Wallet, Calculator, PieChart, FileText,
  Factory, Wrench, ShieldCheck, Truck, Store, PackageCheck, Link2, Car, Globe, ShoppingCart,
  CheckSquare, CheckCircle, CalendarCheck, Building2, ShoppingBasket, AlertTriangle,
  Grid3x3, Briefcase, Cog as CogIcon, CheckCheck, Layout, Check, Play, X, Loader
} from 'lucide-react';

/** All modules selectable on demo */
const DEMO_MODULE_SELECTION_UNLOCKED = new Set(ALL_MODULES.map((m) => m.key));

/** All other modules show the maintenance badge + countdown (staggered per card). */
function stableHash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function buildComingSoonModulesMap() {
  const map = {};
  ALL_MODULES.forEach((m) => {
    if (DEMO_MODULE_SELECTION_UNLOCKED.has(m.key)) return;
    const h = stableHash(m.key);
    const h2 = stableHash(`${m.key}:phase`);
    // Spread 30 min → 48 h so cards never look clustered around the same hour.
    const duration = 1800 + (h % 172800);
    const phase = h2 % duration;
    map[m.key] = { duration, phase };
  });
  return map;
}

const comingSoonModules = buildComingSoonModulesMap();

/**
 * Demo module selection — glass morphism card UI
 */
const ModuleSelection = ({ userData, onSelect }) => {
  const { t } = useTranslation();
  const ms = (key, opts) => t(`onlineDemo.moduleSelection.${key}`, opts);
  const moduleLabel = (moduleKey, field) => t(`onlineDemo.modules.${moduleKey}.${field}`);
  const [selectedModules, setSelectedModules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ hours: 0, minutes: 0, moduleName: '' });

  // Real-time countdown update every second
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000); // Every second
    
    return () => clearInterval(interval);
  }, []);

  // Get countdown for specific module - counts down second by second
  const getModuleCountdown = (moduleKey) => {
    if (!comingSoonModules[moduleKey]) return 0;

    const { duration, phase } = comingSoonModules[moduleKey];
    const nowInSeconds = Math.floor(currentTime / 1000);
    const elapsed = (nowInSeconds + phase) % duration;
    const remaining = duration - elapsed;

    return Math.max(remaining, 900);
  };

  // Format seconds to HH:MM:SS
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Icon mapping: string icon names → Lucide React components
  const iconMap = {
    Receipt, Boxes, TrendingUp, Users, Megaphone, Headphones, ClipboardList, FileSignature,
    UserCog, Clock, DollarSign, Vault, Landmark, Wallet, Calculator, PieChart, FileText,
    Factory, Wrench, ShieldCheck, Truck, Store, PackageCheck, Link2, Car, Globe, ShoppingCart,
    CheckSquare, CheckCircle, CalendarCheck, Building2, ShoppingBasket, AlertTriangle
  };

  const categoryDefs = {
    all: { nameKey: 'categories.all', IconComponent: Grid3x3, modules: ALL_MODULES },
    commercial: { nameKey: 'categories.commercial', IconComponent: Briefcase, modules: ALL_MODULES.filter(m => m.category === 'commercial') },
    rh: { nameKey: 'categories.rh', IconComponent: UserCog, modules: ALL_MODULES.filter(m => m.category === 'rh') },
    finance: { nameKey: 'categories.finance', IconComponent: Calculator, modules: ALL_MODULES.filter(m => m.category === 'finance') },
    logistique: { nameKey: 'categories.logistique', IconComponent: Truck, modules: ALL_MODULES.filter(m => m.category === 'logistique') },
    workflow: { nameKey: 'categories.workflow', IconComponent: CheckCheck, modules: ALL_MODULES.filter(m => m.category === 'workflow') },
  };

  const categories = Object.fromEntries(
    Object.entries(categoryDefs).filter(([key, cat]) => key === 'all' || cat.modules.length > 0)
  );

  const toggleModule = (moduleKey) => {
    // Block selection while module is being updated
    if (comingSoonModules[moduleKey] && !selectedModules.includes(moduleKey)) {
      const countdown = getModuleCountdown(moduleKey);
      const hours = Math.floor(countdown / 3600);
      const minutes = Math.floor((countdown % 3600) / 60);
      const module = ALL_MODULES.find(m => m.key === moduleKey);
      
      setModalData({
        hours,
        minutes,
        moduleName: module ? moduleLabel(module.key, 'name') : ms('modal.this_module')
      });
      setShowModal(true);
      return; // Don't allow selection
    }
    
    setSelectedModules(prev => 
      prev.includes(moduleKey) 
        ? prev.filter(k => k !== moduleKey)
        : [...prev, moduleKey]
    );
  };

  const handleStartDemo = async () => {
    if (selectedModules.length === 0) {
      window.alert(t('onlineDemo.moduleSelection.select_required'));
      return;
    }

    setIsLoading(true);
    
    // Store selected modules
    localStorage.setItem('qawaem_demo_modules', JSON.stringify(selectedModules));
    
    // Analytics
    const analyticsData = {
      ...userData,
      selectedModules: selectedModules,
      moduleCount: selectedModules.length,
      timestamp: new Date().toISOString()
    };
    
    const existingData = JSON.parse(localStorage.getItem('qawaem_demo_analytics') || '[]');
    existingData.push(analyticsData);
    localStorage.setItem('qawaem_demo_analytics', JSON.stringify(existingData));
    
    console.log('📊 Demo Analytics:', analyticsData);
    
    // Delay for animation
    setTimeout(() => {
      onSelect(selectedModules);
    }, 500);
  };

  const displayedModules = (categories[activeCategory] || categories.all).modules;
  const selectedCountLabel = ms(
    selectedModules.length === 1 ? 'selected_count_one' : 'selected_count_other',
    { count: selectedModules.length }
  );

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(29, 71, 196) 50%, rgb(15, 23, 42) 100%)',
      padding: '2rem 1rem',
      paddingBottom: '140px',
      overflowY: 'auto',
      position: 'relative'
    }}>
      {/* Gradient Overlay - Decorative radial gradients with darker edges */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.25) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(30, 58, 138, 0.2) 0%, transparent 50%)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />
      {/* Vignette effect - darker borders */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(ellipse at center, transparent 20%, rgba(10, 15, 30, 0.4) 80%, rgba(10, 15, 30, 0.6) 100%)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />
      {/* Header */}
      <div style={{ maxWidth: '95%', width: '100%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '2rem' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div>
              <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Layout size={32} color="#3B82F6" />
                {ms('title')}
              </h1>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '1rem' }}>
                {ms('subtitle', { count: selectedModules.length, selected: selectedCountLabel })}
              </p>
            </div>
          </div>

          {/* User Info Card */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '1rem',
            padding: '1rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
              borderRadius: '12px',
              padding: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Users size={20} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ color: 'white', fontWeight: '600', fontSize: '1.1rem' }}>{userData.name}</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.5)', margin: '0 0.5rem' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{userData.company}</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.5)', margin: '0 0.5rem' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{userData.phone}</span>
            </div>
          </div>
        </motion.div>

        {/* Category Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{ marginBottom: '2rem' }}
        >
          <div style={{ 
            display: 'flex', 
            gap: '0.75rem', 
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            paddingBottom: '0.5rem'
          }}>
            {Object.keys(categories).map((catKey) => (
              <button
                key={catKey}
                onClick={() => setActiveCategory(catKey)}
                style={{
                  background: activeCategory === catKey 
                    ? 'linear-gradient(135deg, #3B82F6, #2563EB)' 
                    : 'rgba(255, 255, 255, 0.05)',
                  border: `1px solid ${activeCategory === catKey ? '#3B82F6' : 'rgba(255, 255, 255, 0.2)'}`,
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap',
                  backdropFilter: 'blur(12px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  flex: '1 1 auto',
                  minWidth: 'fit-content'
                }}
              >
                {React.createElement(categories[catKey].IconComponent, { size: 18 })}
                {ms(categories[catKey].nameKey)}
                <span style={{ 
                  marginLeft: '0.25rem', 
                  opacity: 0.7,
                  fontSize: '0.85rem'
                }}>
                  ({categories[catKey].modules.length})
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" style={{ marginBottom: '2rem' }}>
          <AnimatePresence mode="sync">
            {displayedModules.map((module, index) => {
              const isSelected = selectedModules.includes(module.key);
              
              return (
                <motion.div
                  key={module.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => toggleModule(module.key)}
                  className="group relative cursor-pointer"
                >
                  <div 
                    className="relative h-full p-5 rounded-2xl border transition-all duration-300 bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-white/30 hover:shadow-2xl hover:shadow-black/20 hover:-translate-y-2"
                    style={{
                      ...(isSelected && {
                        borderColor: `${module.color}80`,
                        boxShadow: `0 0 30px ${module.color}40`
                      })
                    }}
                  >
                    {/* Top accent line */}
                    <div 
                      className="absolute top-0 left-5 right-5 h-1 rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ background: `linear-gradient(90deg, transparent, ${module.color}, transparent)` }}
                    />
                    
                    {/* Icon */}
                    <div className="mb-4">
                      <div 
                        className="p-3 rounded-xl transition-all duration-300 group-hover:scale-110 inline-block"
                        style={{ 
                          background: `linear-gradient(135deg, ${module.color}20, ${module.color}10)`,
                          border: `1px solid ${module.color}30`
                        }}
                      >
                        {iconMap[module.icon] && React.createElement(iconMap[module.icon], { size: 22, color: module.color })}
                      </div>
                    </div>

                    {/* Content */}
                    <div>
                      <h3 className="font-semibold text-white text-base mb-2 group-hover:text-white transition-colors">
                        {moduleLabel(module.key, 'name')}
                      </h3>
                      <p className="text-sm text-white/60 line-clamp-2 leading-relaxed">
                        {moduleLabel(module.key, 'description')}
                      </p>
                    </div>

                    {/* Coming Soon Badge or Selected indicator */}
                    {comingSoonModules[module.key] && !isSelected ? (
                      <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
                        {/* Updating badge */}
                        <div className="px-2.5 py-1 rounded-full flex items-center gap-1.5" style={{ 
                          background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.3), rgba(249, 115, 22, 0.2))',
                          border: '1px solid rgba(251, 146, 60, 0.5)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <span className="text-[9px] font-extrabold uppercase tracking-wider" style={{ 
                            color: '#fb923c', 
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            whiteSpace: 'nowrap'
                          }}>
                            {ms('updating_badge')}
                          </span>
                        </div>
                        
                        {/* Timer badge */}
                        <div className="px-2.5 py-1 rounded-full flex items-center gap-1" style={{ 
                          background: 'rgba(251, 146, 60, 0.15)',
                          border: '1px solid rgba(251, 146, 60, 0.4)',
                          backdropFilter: 'blur(8px)'
                        }}>
                          <Clock size={11} color="#fb923c" />
                          <span className="text-[11px] font-bold tabular-nums" style={{ 
                            color: '#fb923c', 
                            fontFamily: 'monospace',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            letterSpacing: '0.3px'
                          }}>
                            {formatTime(getModuleCountdown(module.key))}
                          </span>
                        </div>
                      </div>
                    ) : isSelected ? (
                      <div className="absolute top-3 right-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: module.color }}>
                          <Check size={14} color="white" strokeWidth={3} />
                        </div>
                      </div>
                    ) : null}

                    {/* Hover glow effect */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{ 
                        background: `radial-gradient(circle at 50% 0%, ${module.color}15 0%, transparent 70%)` 
                      }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Bottom Action Bar - Fixed to viewport bottom */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            position: 'fixed',
            bottom: '1rem',
            left: '2.5%',
            right: '2.5%',
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '1.5rem',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div>
            <p style={{ color: 'white', fontWeight: '600', fontSize: '1.1rem' }}>
              {selectedCountLabel}
            </p>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
              {ms('select_at_least_one')}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={() => setSelectedModules([])}
              disabled={selectedModules.length === 0}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                fontWeight: '600',
                cursor: selectedModules.length === 0 ? 'not-allowed' : 'pointer',
                opacity: selectedModules.length === 0 ? 0.5 : 1,
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <X size={18} />
              {ms('deselect_all')}
            </button>

            <button
              onClick={handleStartDemo}
              disabled={isLoading || selectedModules.length === 0}
              style={{
                background: selectedModules.length > 0 
                  ? 'linear-gradient(135deg, #3B82F6, #2563EB)' 
                  : 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '1rem',
                cursor: selectedModules.length === 0 ? 'not-allowed' : 'pointer',
                opacity: selectedModules.length === 0 ? 0.5 : 1,
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {isLoading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  <span>{t('onlineDemo.moduleSelection.loading')}</span>
                </>
              ) : (
                <>
                  <Play size={18} />
                  <span>{ms('start_demo')}</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Modal for module-updating notification */}
      <AnimatePresence>
        {showModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(8px)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem'
              }}
            >
              {/* Modal Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: 'spring', duration: 0.5 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))',
                  border: '1px solid rgba(251, 146, 60, 0.3)',
                  borderRadius: '1.5rem',
                  padding: '2.5rem',
                  maxWidth: '500px',
                  width: '100%',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(20px)',
                  position: 'relative'
                }}
              >
                {/* Close button */}
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '0.5rem',
                    width: '2rem',
                    height: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  <X size={18} />
                </button>

                {/* Warning Icon */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  marginBottom: '1.5rem' 
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.2), rgba(249, 115, 22, 0.1))',
                    border: '2px solid rgba(251, 146, 60, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Clock size={40} color="#fb923c" />
                  </div>
                </div>

                {/* Title */}
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: 'bold',
                  color: 'white',
                  textAlign: 'center',
                  marginBottom: '1rem'
                }}>
                  {ms('modal.title')}
                </h2>

                {/* Module name */}
                <p style={{
                  fontSize: '1.1rem',
                  color: '#fb923c',
                  textAlign: 'center',
                  fontWeight: '600',
                  marginBottom: '1.5rem'
                }}>
                  {modalData.moduleName}
                </p>

                {/* Timer */}
                <div style={{
                  background: 'rgba(251, 146, 60, 0.1)',
                  border: '1px solid rgba(251, 146, 60, 0.3)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem'
                  }}>
                    <Clock size={24} color="#fb923c" />
                    <div>
                      <p style={{
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.6)',
                        marginBottom: '0.25rem'
                      }}>
                        {ms('modal.available_in')}
                      </p>
                      <p style={{
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        color: '#fb923c',
                        fontFamily: 'monospace',
                        letterSpacing: '2px'
                      }}>
                        {ms('modal.time_format', { hours: modalData.hours, minutes: modalData.minutes })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Message */}
                <p style={{
                  fontSize: '1rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  textAlign: 'center',
                  lineHeight: '1.6',
                  marginBottom: '2rem'
                }}>
                  <strong style={{ color: '#10B981' }}>{ms('modal.built_message')}</strong>
                  <br /><br />
                  {ms('modal.update_message')}
                </p>

                {/* OK Button */}
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #fb923c, #f97316)',
                    border: 'none',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 12px rgba(251, 146, 60, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(251, 146, 60, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(251, 146, 60, 0.4)';
                  }}
                >
                  {ms('modal.ok')}
                </button>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModuleSelection;

